import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../i18n";

const useCountUp = (target, active) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active || !target) return;
    let raf;
    const start = performance.now();
    const dur = 1400;
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.floor(eased * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, active]);
  return val;
};

export const Stats = () => {
  const { t, lang } = useI18n();
  const [stats, setStats] = useState({ supporters: 0, volunteers: 0, departments: 0 });
  const [active, setActive] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const load = () => api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    load();
    window.addEventListener("supporter-added", load);
    return () => window.removeEventListener("supporter-added", load);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => e.isIntersecting && setActive(true), { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const supporters = useCountUp(stats.supporters, active);
  const volunteers = useCountUp(stats.volunteers, active);
  const departments = useCountUp(stats.departments, active);

  const items = [
    { label: t("stats.supporters"), value: supporters, testid: "stat-supporters" },
    { label: t("stats.volunteers"), value: volunteers, testid: "stat-volunteers" },
    { label: t("stats.departments"), value: departments, testid: "stat-departments" },
  ];

  return (
    <section id="chiffres" ref={ref} className="bg-[#0B1121] py-16 sm:py-20 relative overflow-hidden">
      <div className="absolute inset-0 grain opacity-[0.15] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative grid grid-cols-1 sm:grid-cols-3 gap-10">
        {items.map((it) => (
          <div key={it.testid} data-testid={it.testid} className="text-center sm:text-left">
            <div className="font-mono-accent text-5xl sm:text-6xl font-semibold text-white tabular-nums">
              {it.value.toLocaleString(lang === "ar" ? "ar-EG" : lang)}
            </div>
            <div className="mt-2 font-mono-accent text-xs uppercase tracking-[0.3em] text-[#38BDF8]">
              {it.label}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
